import { QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { toast } from 'sonner';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';

import { App } from '@/App';
import { Toaster, TooltipProvider } from '@/components/cargofl-ui';
import { applyAppearance, readStoredAppearance } from '@/lib/appearance';
import { ApiError } from '@/lib/api';
import { SessionProvider } from '@/lib/session';
import { ThemeProvider } from '@/lib/theme';
import '@/styles/globals.css';

const queryClient = new QueryClient({
  /**
   * A failed read must not render as an empty one.
   *
   * Every list here goes through `data ?? []`, so a query that 403s and one that
   * genuinely has no rows paint identically. That is not a cosmetic problem: the asset
   * type picker sat behind a permission plain employees do not hold, and for months the
   * "Request an asset" form simply offered no types - no error, no spinner, nothing to
   * report to IT. It looked like a form with an empty dropdown rather than a broken one.
   *
   * A toast is the honest floor. It does not fix the screen, but it makes a load
   * failure say so, and a 401 is left alone because the session provider is already
   * redirecting to the login page.
   */
  queryCache: new QueryCache({
    onError: (error) => {
      if (error instanceof ApiError && error.status === 401) return;
      const detail = error instanceof ApiError ? error.message : 'Check your connection.';
      toast.error('Something on this page could not be loaded.', { description: detail });
    },
  }),
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      // Retrying a 401/403/404 just delays showing the user the real answer.
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/**
 * Re-apply the saved accent and font here, not in the panel that edits them.
 *
 * The panel lives inside a Radix dropdown, which does not mount until the menu
 * is opened - so anything it did on mount was not "on load" at all. The inline
 * script in index.html replays the last-painted values before first paint, but
 * it is a dumb replayer: a cache written by an older build is missing whatever
 * that build did not paint, and only this module knows how to derive the rest.
 * That gap is what left the sidebar on the brand hue after the rail started
 * following the accent.
 */
applyAppearance(readStoredAppearance());

const container = document.getElementById('root');
if (!container) throw new Error('#root not found in index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        {/* Inside QueryClientProvider: the session is itself a query, so the cache
            must exist before it runs. */}
        <SessionProvider>
          {/* One provider for the whole app - Radix Tooltip throws without one, and
              the sidebar's local provider only covers the sidebar. */}
          <TooltipProvider>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </TooltipProvider>
          <Toaster position="bottom-right" />
        </SessionProvider>
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
);
